import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useFirestore } from '../hooks/useFirestore';
import { useConfirmation } from '../hooks/useConfirmation';
import ArticleCard from '../components/Articles/ArticleCard';
import ArticleModal from '../components/Articles/ArticleModal';
import ConfirmationModal from '../components/Common/ConfirmationModal';
import Breadcrumbs from '../components/Navigation/Breadcrumbs';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiFilter, FiTrash2 } = FiIcons;

const Articles = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgency, setFilterAgency] = useState('all');
  const [filterDomain, setFilterDomain] = useState('all');

  const { documents: articles, loading: articlesLoading, addDocument: addArticle, updateDocument: updateArticle, deleteDocument: deleteArticle } = useFirestore('articles');
  const { documents: agencies, loading: agenciesLoading, updateDocument: updateAgency } = useFirestore('agencies');
  const { documents: domains, loading: domainsLoading } = useFirestore('domains');
  const { confirmationState, showConfirmation, closeConfirmation } = useConfirmation();

  const handleAddArticle = () => {
    setEditingArticle(null);
    setIsModalOpen(true);
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setIsModalOpen(true);
  };

  const handleSaveArticle = async (articleData) => {
    try {
      if (editingArticle) {
        await updateArticle(editingArticle.id, articleData);
        toast.success('Article updated successfully!');
      } else {
        await addArticle(articleData);
        
        // Update agency remaining articles count only for prepaid agencies
        const selectedAgency = agencies.find(agency => agency.id === articleData.agency_id);
        if (selectedAgency && selectedAgency.payment_type === 'prepaid' && selectedAgency.remaining_articles > 0) {
          await updateAgency(selectedAgency.id, {
            remaining_articles: selectedAgency.remaining_articles - 1
          });
        }
        
        toast.success('Article created successfully!');
      }
      setIsModalOpen(false);
      setEditingArticle(null);
    } catch (error) {
      toast.error('Error saving article: ' + error.message);
    }
  };

  const handleDeleteArticle = async (article) => {
    const confirmed = await showConfirmation({
      title: 'Delete Article',
      message: `Are you sure you want to delete "${article.title}"? This action cannot be undone.`,
      confirmText: 'Delete Article',
      cancelText: 'Keep Article',
      type: 'danger',
      icon: FiTrash2,
      onConfirm: async () => {
        await deleteArticle(article.id);
        
        // Restore agency remaining articles count only for prepaid agencies
        const selectedAgency = agencies.find(agency => agency.id === article.agency_id);
        if (selectedAgency && selectedAgency.payment_type === 'prepaid') {
          await updateAgency(selectedAgency.id, {
            remaining_articles: selectedAgency.remaining_articles + 1
          });
        }
        
        toast.success('Article deleted successfully!');
      }
    });

    if (!confirmed) {
      toast.error('Failed to delete article');
    }
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency ? agency.name : 'Unknown Agency';
  };

  const getDomainName = (domainId) => {
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.name : 'Unknown Domain';
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.article_url && article.article_url.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' || article.status === filterStatus;
    const matchesAgency = filterAgency === 'all' || article.agency_id === filterAgency;
    const matchesDomain = filterDomain === 'all' || article.domain_id === filterDomain;
    
    return matchesSearch && matchesStatus && matchesAgency && matchesDomain;
  });

  const totalArticles = articles.length;
  const publishedArticles = articles.filter(article => article.status === 'published').length;
  const completedArticles = articles.filter(article => article.status === 'completed').length;
  const inProgressArticles = articles.filter(article => article.status === 'in_progress').length;
  const draftArticles = articles.filter(article => article.status === 'draft').length;

  // Calculate billing information for postpaid agencies
  const postpaidEarnings = articles.reduce((sum, article) => {
    const agency = agencies.find(a => a.id === article.agency_id);
    if (agency && agency.payment_type === 'postpaid' && article.status === 'published') {
      return sum + (agency.price_per_article || 0);
    }
    return sum;
  }, 0);

  const loading = articlesLoading || agenciesLoading || domainsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Articles</h1>
          <p className="text-gray-600">Manage published articles for your agency clients</p>
        </div>
        <button
          onClick={handleAddArticle}
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>Add Article</span>
        </button>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Articles</h3>
          <p className="text-3xl font-bold text-primary-600">{totalArticles}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Published</h3>
          <p className="text-3xl font-bold text-success-600">{publishedArticles}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Completed</h3>
          <p className="text-3xl font-bold text-primary-600">{completedArticles}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">In Progress</h3>
          <p className="text-3xl font-bold text-warning-600">{inProgressArticles}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Drafts</h3>
          <p className="text-3xl font-bold text-gray-600">{draftArticles}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">To Bill</h3>
          <p className="text-3xl font-bold text-purple-600">{postpaidEarnings.toFixed(2)} LEI</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search articles by title or URL..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-4">
          <div className="relative">
            <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="published">Published</option>
            </select>
          </div>

          <select
            value={filterAgency}
            onChange={(e) => setFilterAgency(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="all">All Agencies</option>
            {agencies.map(agency => (
              <option key={agency.id} value={agency.id}>
                {agency.name} ({agency.payment_type})
              </option>
            ))}
          </select>

          <select
            value={filterDomain}
            onChange={(e) => setFilterDomain(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="all">All Domains</option>
            {domains.map(domain => (
              <option key={domain.id} value={domain.id}>
                {domain.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredArticles.map((article, index) => (
          <motion.div
            key={article.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <ArticleCard
              article={article}
              agencyName={getAgencyName(article.agency_id)}
              domainName={getDomainName(article.domain_id)}
              onEdit={handleEditArticle}
              onDelete={() => handleDeleteArticle(article)}
            />
          </motion.div>
        ))}
      </div>

      {filteredArticles.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-gray-500 text-lg">No articles found</p>
          <p className="text-gray-400 mt-2">
            {searchTerm || filterStatus !== 'all' || filterAgency !== 'all' || filterDomain !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Add your first article to get started'}
          </p>
        </motion.div>
      )}

      <ArticleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveArticle}
        article={editingArticle}
        agencies={agencies}
        domains={domains}
      />

      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        icon={confirmationState.icon}
        isLoading={confirmationState.isLoading}
      />
    </div>
  );
};

export default Articles;